export interface HomepageSectionHeading {
  eyebrow?: string
  title: string
  subtitle?: string
}

export type HomepageSectionKey = 'categories' | 'featured' | 'features' | 'reviews'

export type HomepageSections = Record<HomepageSectionKey, HomepageSectionHeading>

export const SERVICES_HOMEPAGE_CATEGORIES_HEADING: HomepageSectionHeading = {
  eyebrow: 'Explorează',
  title: 'Serviciile noastre',
  subtitle: 'Servicii cu preț clar pe unitate.',
}

export const DEFAULT_HOMEPAGE_SECTIONS: HomepageSections = {
  categories: {
    eyebrow: 'Explorează',
    title: 'Categorii populare',
    subtitle: 'Descoperă oferta noastră, organizată pe categorii.',
  },
  featured: {
    eyebrow: 'Selecția noastră',
    title: 'Produse recomandate',
    subtitle: 'Cele mai căutate produse, alese pentru tine.',
  },
  features: {
    eyebrow: 'De ce noi',
    title: 'Cumpără cu încredere',
    subtitle: '',
  },
  reviews: {
    eyebrow: 'Recenzii',
    title: 'Ce spun clienții noștri',
    subtitle: 'Recenzii reale de pe Google Business.',
  },
}

export function resolveServicesHomepageHeading(
  heading: HomepageSectionHeading,
): HomepageSectionHeading {
  const fallback = DEFAULT_HOMEPAGE_SECTIONS.categories
  return {
    eyebrow: heading.eyebrow,
    title: heading.title === fallback.title
      ? SERVICES_HOMEPAGE_CATEGORIES_HEADING.title
      : heading.title,
    subtitle: heading.subtitle === fallback.subtitle
      ? SERVICES_HOMEPAGE_CATEGORIES_HEADING.subtitle
      : heading.subtitle,
  }
}

function normalizeHeading(raw: unknown, fallback: HomepageSectionHeading): HomepageSectionHeading {
  if (!raw || typeof raw !== 'object') return { ...fallback }

  const item = raw as Record<string, unknown>
  const title = String(item.title ?? '').trim()

  return {
    eyebrow: String(item.eyebrow ?? '').trim() || undefined,
    title: title || fallback.title,
    subtitle: String(item.subtitle ?? '').trim() || undefined,
  }
}

export function parseHomepageSections(
  raw?: string | null,
  fallback: HomepageSections = DEFAULT_HOMEPAGE_SECTIONS,
): HomepageSections {
  if (!raw) {
    return {
      categories: { ...fallback.categories },
      featured: { ...fallback.featured },
      features: { ...fallback.features },
      reviews: { ...fallback.reviews },
    }
  }

  try {
    const parsed = JSON.parse(raw) as Partial<Record<HomepageSectionKey, unknown>>
    if (!parsed || typeof parsed !== 'object') {
      return {
        categories: { ...fallback.categories },
        featured: { ...fallback.featured },
        features: { ...fallback.features },
        reviews: { ...fallback.reviews },
      }
    }

    return {
      categories: normalizeHeading(parsed.categories, fallback.categories),
      featured: normalizeHeading(parsed.featured, fallback.featured),
      features: normalizeHeading(parsed.features, fallback.features),
      reviews: normalizeHeading(parsed.reviews, fallback.reviews),
    }
  } catch {
    return {
      categories: { ...fallback.categories },
      featured: { ...fallback.featured },
      features: { ...fallback.features },
      reviews: { ...fallback.reviews },
    }
  }
}
